// lib/apiClient.ts
import axios from "axios";
import { API_KEY, BASE_URL } from "../utils/config";
import useStore from "../store/store";
import { toast } from "react-hot-toast";
import Router from "next/router";

// Create Axios instance
export const apiClient = axios.create({
  baseURL: BASE_URL as string,
  withCredentials: true,
});

// ✅ Utility Toasts
const showSuccessToast = (message: unknown): void => {
  if (typeof message === "string") toast.success(message);
};

const showErrorToast = (error: any): void => {
  if (
    axios.isAxiosError(error) &&
    (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK")
  ) {
    return;
  }
  toast.error(typeof error === "string" ? error : "An error occurred");
};

// ✅ Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const { activeLanguage, accessToken } = useStore.getState();

    // add language
    if (activeLanguage) {
      config.headers["Accept-Language"] = activeLanguage;
    }

    // add token
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem("userInfo");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user?.token) {
          config.headers["Authorization"] = `Bearer ${user.token}`;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Format Error
export const formatError = (err: any) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.detail)) {
    return data.detail.map((item: any) => item.msg).join("\n");
  }

  const serverMessage =
    data?.message || data?.error || data?.detail || JSON.stringify(data);

  return typeof serverMessage === "string"
    ? serverMessage
    : JSON.stringify(serverMessage);
};

// ✅ Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (
      (response.config.method === "post" ||
        response.config.method === "put") &&
      response.data?.message
    ) {
      const message =
        typeof response.data.message === "object"
          ? response.data.message?.message || "Successful"
          : response.data.message || response.data.detail || "Successful";

      showSuccessToast(message);
    }
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      const serverMessage = formatError(error);
      console.error("Server message:", serverMessage);

      if (
        error.config.method === "post" ||
        error.config.method === "put"
      ) {
        showErrorToast(
          error.response?.status === 403 ? "Forbidden" : serverMessage
        );
      }
    } else {
      console.error("Unexpected error:", error);
    }

    return Promise.reject(error);
  }
);

// ✅ Handle 401 Unauthorized globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        showErrorToast(
         "Session expired"
        );
        useStore.getState().clearUserInfo?.();
        if (typeof window !== "undefined") {
          localStorage.removeItem("userInfo");
        }
        window.location.href = "/";
      } catch (logoutError) {
        console.error("Error during automatic logout:", logoutError);
      }
    }
    return Promise.reject(error);
  }
);
